/*
  Ceramic Aura collection page (sections/ca-collection.liquid)

  - Filter drawer: opens from the bar, results update live behind it, focus stays inside while open.
  - Sort, filter chips, Clear all: re-render the page parts with the Section Rendering API, then scroll
    back to the start of the results if the visitor was further down (the bar is sticky below 1200px).
  - Load more: appends the next page to the grid.
  - Swatches: swap the card photo, price, sold-out badge and links to the chosen style.
  - Sub-category rows (photo tiles, text tabs): mark the ends that hide items and page with the arrows.
  Without JS every control is still a working link or form, and the rows still scroll.
*/
(function () {
  if (window.CaCollection) return;

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function plainClick(event) {
    return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
  }

  function cleanUrl(href) {
    var url = new URL(href, location.href);
    url.searchParams.delete('section_id');
    return url;
  }

  function CaCollection(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-section-id');
    this.drawer = root.querySelector('[data-cc-drawer]');
    this.controller = null;
    this.hideTimer = 0;
    this.changed = false;

    if (this.drawer) {
      this.drawer.setAttribute('data-cc-owner', this.sectionId);
      document.body.appendChild(this.drawer);
    }

    this.onRootClick = this.onRootClick.bind(this);
    this.onDocClick = this.onDocClick.bind(this);
    this.onRootOver = this.onRootOver.bind(this);
    this.onDrawerClick = this.onDrawerClick.bind(this);
    this.onDrawerChange = this.onDrawerChange.bind(this);
    this.onDrawerSubmit = this.onDrawerSubmit.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onResize = this.onResize.bind(this);

    root.addEventListener('click', this.onRootClick);
    document.addEventListener('click', this.onDocClick);
    root.addEventListener('pointerover', this.onRootOver);
    if (this.drawer) {
      this.drawer.addEventListener('click', this.onDrawerClick);
      this.drawer.addEventListener('change', this.onDrawerChange);
      this.drawer.addEventListener('submit', this.onDrawerSubmit);
    }
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('resize', this.onResize);

    root.classList.add('cc--js');
    this.setupIntro();
    this.rails = Array.prototype.map.call(root.querySelectorAll('[data-cc-rail]'), function (nav) {
      return new Rail(nav);
    });
  }

  CaCollection.prototype.destroy = function () {
    this.root.removeEventListener('click', this.onRootClick);
    document.removeEventListener('click', this.onDocClick);
    this.root.removeEventListener('pointerover', this.onRootOver);
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.onResize);
    document.documentElement.classList.remove('cc-lock');
    this.rails.forEach(function (rail) { rail.destroy(); });
    if (this.controller) this.controller.abort();
    if (this.drawer) this.drawer.remove();
  };

  /* ---------- Intro ---------- */

  CaCollection.prototype.setupIntro = function () {
    var self = this;
    this.introText = this.root.querySelector('[data-cc-intro-text]');
    this.introToggle = this.root.querySelector('[data-cc-intro-toggle]');
    if (!this.introText || !this.introToggle) return;

    this.introToggle.addEventListener('click', function () {
      self.introOpen = true;
      self.introText.classList.remove('is-clamped');
      self.introToggle.hidden = true;
      self.introText.setAttribute('tabindex', '-1');
      self.introText.focus({ preventScroll: true });
    });

    this.measureIntro();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.measureIntro.bind(this));
  };

  CaCollection.prototype.measureIntro = function () {
    if (!this.introText || this.introOpen) return;
    this.introText.classList.add('is-clamped');
    var overflowing = this.introText.scrollHeight > this.introText.clientHeight + 2;
    this.introText.classList.toggle('is-clamped', overflowing);
    this.introToggle.hidden = !overflowing;
  };

  CaCollection.prototype.onResize = function () {
    var self = this;
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(function () {
      self.measureIntro();
      self.rails.forEach(function (rail) { rail.update(); });
    }, 150);
  };

  /* ---------- Sub-category rows ---------- */

  /*
    A row of photo tiles or text tabs that scrolls sideways. data-more on the row lists the ends that
    still hide items; the CSS fades those ends and shows the arrow there. An arrow pages to the first
    item it hides, lined up with the row's start (after the fade, for the tabs).
  */
  function Rail(nav) {
    this.nav = nav;
    this.list = nav.querySelector('[data-cc-rail-list]');
    this.prev = nav.querySelector('[data-cc-rail-prev]');
    this.next = nav.querySelector('[data-cc-rail-next]');
    this.tabs = nav.getAttribute('data-cc-rail') === 'tabs';
    this.update = this.update.bind(this);
    this.onPrev = this.page.bind(this, -1);
    this.onNext = this.page.bind(this, 1);

    this.list.addEventListener('scroll', this.update, { passive: true });
    if (this.prev) this.prev.addEventListener('click', this.onPrev);
    if (this.next) this.next.addEventListener('click', this.onNext);
    if (window.ResizeObserver) {
      this.observer = new ResizeObserver(this.update);
      this.observer.observe(this.list);
    }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.update);
    this.update();
  }

  Rail.prototype.destroy = function () {
    this.list.removeEventListener('scroll', this.update);
    if (this.prev) this.prev.removeEventListener('click', this.onPrev);
    if (this.next) this.next.removeEventListener('click', this.onNext);
    if (this.observer) this.observer.disconnect();
  };

  Rail.prototype.update = function () {
    var list = this.list;
    var max = list.scrollWidth - list.clientWidth;
    // Below 1200px the photo row runs to the screen edge, so a tile that only reaches into the side padding
    // is still fully shown; the tabs are faded there, so for them any overflow counts.
    var style = this.tabs ? null : getComputedStyle(list);
    var padStart = style ? parseFloat(style.paddingLeft) || 0 : 0;
    var padEnd = style ? parseFloat(style.paddingRight) || 0 : 0;
    var atStart = list.scrollLeft <= padStart + 1;
    var atEnd = list.scrollLeft >= max - padEnd - 1;
    var more = [];
    if (!atStart) more.push('start');
    if (!atEnd) more.push('end');
    var value = more.join(' ');
    if (this.nav.getAttribute('data-more') === value) return;
    this.nav.setAttribute('data-more', value);

    // An arrow hides once its end is reached; hand keyboard focus to the other one rather than dropping it.
    var active = document.activeElement;
    if (active === this.next && atEnd && !atStart) this.prev.focus({ preventScroll: true });
    if (active === this.prev && atStart && !atEnd) this.next.focus({ preventScroll: true });
  };

  Rail.prototype.page = function (direction) {
    var list = this.list;
    var style = getComputedStyle(list);
    var fade = this.tabs ? parseFloat(getComputedStyle(this.nav).getPropertyValue('--cc-rail-fade')) || 0 : 0;
    var cover = this.tabs && this.prev ? this.prev.offsetWidth : 0;
    var box = list.getBoundingClientRect();
    var start = box.left + Math.max(parseFloat(style.paddingLeft) || 0, cover) + fade;
    var end = box.right - Math.max(parseFloat(style.paddingRight) || 0, cover) - fade;
    var items = list.children;
    var shift = null;
    var i, j, rect;

    if (direction > 0) {
      for (i = 0; i < items.length; i++) {
        rect = items[i].getBoundingClientRect();
        if (rect.right > end + 1) {
          shift = rect.left - start;
          break;
        }
      }
    } else {
      for (i = items.length - 1; i >= 0; i--) {
        rect = items[i].getBoundingClientRect();
        if (rect.left < start - 1) {
          // Bring this item in at the far end, then move on to the first item that starts at or after the near end.
          shift = rect.right - end;
          for (j = 0; j <= i; j++) {
            var left = items[j].getBoundingClientRect().left;
            if (left >= start + shift - 1) {
              shift = left - start;
              break;
            }
          }
          break;
        }
      }
    }
    if (shift === null) return;

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    list.scrollTo({ left: Math.max(0, list.scrollLeft + shift), behavior: reduce ? 'auto' : 'smooth' });
  };

  // Scrolls a row that overflows so the item sits in the middle, where the snippet's inline script starts the row of that item's page.
  Rail.prototype.center = function (item, smooth) {
    var list = this.list;
    var max = list.scrollWidth - list.clientWidth;
    if (max <= 0) return;
    var box = list.getBoundingClientRect();
    var rect = item.getBoundingClientRect();
    var left = Math.max(0, Math.min(max, rect.left - box.left + list.scrollLeft - (box.width - rect.width) / 2));
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    list.scrollTo({ left: left, behavior: smooth && !reduce ? 'smooth' : 'auto' });
  };

  /* ---------- Events ---------- */

  CaCollection.prototype.onRootClick = function (event) {
    var target = event.target;

    var open = target.closest('[data-cc-open]');
    if (open) {
      event.preventDefault();
      this.openDrawer(open);
      return;
    }

    var swatch = target.closest('[data-cc-swatch]');
    if (swatch && plainClick(event)) {
      event.preventDefault();
      this.selectSwatch(swatch);
      return;
    }

    var next = target.closest('[data-cc-next]');
    if (next && plainClick(event)) {
      event.preventDefault();
      this.loadMore(next);
      return;
    }

    var refresh = target.closest('[data-cc-refresh]');
    if (refresh && plainClick(event)) {
      event.preventDefault();
      this.toggleSort(false);
      this.refresh(refresh.href, { from: refresh });
    }
  };

  CaCollection.prototype.onRootOver = function (event) {
    if (event.pointerType !== 'mouse') return;
    var swatch = event.target.closest('[data-cc-swatch]');
    if (!swatch || swatch.hasAttribute('data-cc-preloaded')) return;
    swatch.setAttribute('data-cc-preloaded', '');
    var card = swatch.closest('[data-cc-card]');
    var img = card && card.querySelector('[data-cc-img]');
    if (!img) return;
    var preload = new Image();
    preload.sizes = img.sizes;
    preload.srcset = swatch.getAttribute('data-srcset');
    preload.src = swatch.getAttribute('data-src');
  };

  CaCollection.prototype.onDrawerClick = function (event) {
    if (event.target.closest('[data-cc-close]')) {
      event.preventDefault();
      this.closeDrawer();
      return;
    }
    var refresh = event.target.closest('[data-cc-refresh]');
    if (refresh && plainClick(event)) {
      event.preventDefault();
      this.refresh(refresh.href, { from: refresh });
    }
  };

  CaCollection.prototype.onDrawerChange = function (event) {
    if (!event.target.closest('[data-cc-form]')) return;
    this.refresh(this.formUrl(), { focusId: event.target.id });
  };

  CaCollection.prototype.onDrawerSubmit = function (event) {
    event.preventDefault();
    var url = this.formUrl();
    if (url !== this.lastUrl) this.refresh(url);
    this.closeDrawer();
  };

  /* ---------- Sort panel ---------- */

  // <summary> opens and closes the panel by itself, so the script only ever needs to close it:
  // on a click elsewhere, on Escape, and before a refresh swaps the control out.
  CaCollection.prototype.onDocClick = function (event) {
    // This instance's own panel, not any panel: with two collection sections on a page, opening one
    // has to close the other.
    var panel = this.root.querySelector('[data-cc-sort][open]');
    if (panel && !panel.contains(event.target)) this.toggleSort(false);
  };

  CaCollection.prototype.toggleSort = function (open) {
    var panel = this.root.querySelector('[data-cc-sort]');
    if (!panel || panel.open === open) return;
    panel.open = open;
  };

  CaCollection.prototype.onKeydown = function (event) {
    // Escape closes the sort panel first; only when no panel is open does it fall through to the drawer.
    if (event.key === 'Escape') {
      var openSort = this.root.querySelector('[data-cc-sort][open]');
      if (openSort) {
        event.preventDefault();
        this.toggleSort(false);
        var summary = openSort.querySelector('[data-cc-sort-button]');
        if (summary) summary.focus();
        return;
      }
    }
    if (!this.drawer || this.drawer.hidden || !this.drawer.classList.contains('is-open')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDrawer();
      return;
    }
    if (event.key !== 'Tab') return;
    var panel = this.drawer.querySelector('[data-cc-form]');
    var items = Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetWidth || el.offsetHeight || el.getClientRects().length;
    });
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (!panel.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* ---------- Drawer ---------- */

  CaCollection.prototype.openDrawer = function (opener) {
    if (!this.drawer) return;
    var drawer = this.drawer;
    clearTimeout(this.hideTimer);
    this.changed = false;
    drawer.hidden = false;
    void drawer.offsetWidth;
    drawer.classList.add('is-open');
    document.documentElement.classList.add('cc-lock');
    opener.setAttribute('aria-expanded', 'true');
    var close = drawer.querySelector('.cc-drawer__close');
    setTimeout(function () {
      if (close) close.focus({ preventScroll: true });
    }, 60);
  };

  CaCollection.prototype.closeDrawer = function () {
    if (!this.drawer || !this.drawer.classList.contains('is-open')) return;
    var drawer = this.drawer;
    drawer.classList.remove('is-open');
    document.documentElement.classList.remove('cc-lock');
    clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(function () {
      if (!drawer.classList.contains('is-open')) drawer.hidden = true;
    }, 420);

    var opener = this.root.querySelector('[data-cc-open]');
    if (opener) {
      opener.setAttribute('aria-expanded', 'false');
      opener.focus({ preventScroll: true });
    }

    if (this.changed) {
      this.changed = false;
      this.revealResults();
    }
  };

  // After the results change, bring their start back into view if the visitor had scrolled past it.
  CaCollection.prototype.revealResults = function () {
    var bar = this.root.querySelector('[data-cc-bar]');
    if (!bar) return;
    // Minimog's sticky header is a fixed wrapper that slides back in on scroll-up, so reserve its full height.
    var header = document.querySelector('.m-header__wrapper');
    var headerHeight = header && /^(fixed|sticky)$/.test(getComputedStyle(header).position) ? header.offsetHeight : 0;
    var anchor = bar;
    var offset = headerHeight + 8;
    if (getComputedStyle(bar).position === 'sticky') {
      // A sticky bar never leaves the screen, so line up what follows it (the filter chips, then the grid) under it.
      anchor = this.root.querySelector('[data-cc-active]') || this.root.querySelector('[data-cc-results]') || bar;
      offset = headerHeight + bar.offsetHeight;
    }
    var top = anchor.getBoundingClientRect().top;
    if (top < offset - 1) {
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: window.scrollY + top - offset, behavior: reduce ? 'auto' : 'smooth' });
    }
  };

  CaCollection.prototype.formUrl = function () {
    var form = this.drawer.querySelector('[data-cc-form]');
    var params = new URLSearchParams();
    new FormData(form).forEach(function (value, key) {
      if (value !== '') params.append(key, value);
    });
    var url = new URL(form.getAttribute('action'), location.href);
    url.search = params.toString();
    return url.href;
  };

  /* ---------- Section rendering ---------- */

  CaCollection.prototype.fetchSection = function (href, signal) {
    var url = cleanUrl(href);
    url.searchParams.set('section_id', this.sectionId);
    return fetch(url.href, { credentials: 'same-origin', signal: signal })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        doc.querySelectorAll('a[href*="section_id="]').forEach(function (link) {
          var clean = cleanUrl(link.getAttribute('href'));
          link.setAttribute('href', clean.pathname + clean.search);
        });
        return doc;
      });
  };

  CaCollection.prototype.refresh = function (href, options) {
    options = options || {};
    var self = this;
    var target = cleanUrl(href);
    target.searchParams.delete('page');
    this.lastUrl = target.href;
    if (this.drawer && this.drawer.classList.contains('is-open')) this.changed = true;

    if (this.controller) this.controller.abort();
    var controller = new AbortController();
    this.controller = controller;

    var results = this.root.querySelector('[data-cc-results]');
    if (results) results.setAttribute('aria-busy', 'true');

    var activeRegion = this.root.querySelector('[data-cc-active]');
    var focusWasInActive = activeRegion && activeRegion.contains(document.activeElement);
    var focusWasInDrawer = this.drawer && this.drawer.contains(document.activeElement);
    var focusId = options.focusId || (document.activeElement && document.activeElement.id) || '';

    this.fetchSection(target.href, controller.signal)
      .then(function (doc) {
        if (controller.signal.aborted) return;
        self.apply(doc);
        history.replaceState(history.state, '', target.pathname + target.search + location.hash);

        var focusTarget = focusId && document.getElementById(focusId);
        if (focusTarget && focusTarget !== document.activeElement) {
          focusTarget.focus({ preventScroll: true });
        } else if (focusWasInActive) {
          var fallback = self.root.querySelector('[data-cc-active] a') || self.root.querySelector('[data-cc-open]');
          if (fallback) fallback.focus({ preventScroll: true });
        } else if (focusWasInDrawer && !self.drawer.contains(document.activeElement)) {
          var apply = self.drawer.querySelector('[data-cc-apply]');
          if (apply) apply.focus({ preventScroll: true });
        }

        // While the drawer is open the page stays put; closeDrawer scrolls once it shuts.
        if (!self.drawer || !self.drawer.classList.contains('is-open')) self.revealResults();
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        location.assign(target.href);
      })
      .then(function () {
        if (self.controller !== controller) return;
        self.controller = null;
        var current = self.root.querySelector('[data-cc-results]');
        if (current) current.removeAttribute('aria-busy');
      });
  };

  CaCollection.prototype.apply = function (doc) {
    var fresh = doc.querySelector('[data-cc]');
    if (!fresh) throw new Error('Section missing');

    this.syncBar(fresh.querySelector('[data-cc-bar]'));

    var oldActive = this.root.querySelector('[data-cc-active]');
    var newActive = fresh.querySelector('[data-cc-active]');
    if (oldActive && newActive) oldActive.replaceWith(newActive);

    var oldResults = this.root.querySelector('[data-cc-results]');
    var newResults = fresh.querySelector('[data-cc-results]');
    if (oldResults && newResults) oldResults.replaceWith(newResults);

    var newDrawer = fresh.querySelector('[data-cc-drawer]');
    if (this.drawer && newDrawer) this.syncDrawer(newDrawer);
  };

  CaCollection.prototype.syncBar = function (newBar) {
    var oldBar = this.root.querySelector('[data-cc-bar]');
    if (!oldBar || !newBar) return;
    var oldOpen = oldBar.querySelector('[data-cc-open]');
    var newOpen = newBar.querySelector('[data-cc-open]');
    var oldCount = oldBar.querySelector('.cc-bar__count');
    var newCount = newBar.querySelector('.cc-bar__count');
    var oldSort = oldBar.querySelector('[data-cc-sort]');
    var newSort = newBar.querySelector('[data-cc-sort]');

    if (!!oldOpen !== !!newOpen || !oldCount || !newCount || !!oldSort !== !!newSort) {
      oldBar.replaceWith(newBar);
      return;
    }
    /* Every sort link carries the filters that are on, so the whole control is swapped, not just
       the name on the button. */
    if (oldSort && newSort) oldSort.replaceWith(newSort);
    if (oldOpen) {
      var oldBadge = oldOpen.querySelector('.cc-bar__badge');
      var newBadge = newOpen.querySelector('.cc-bar__badge');
      if (oldBadge) oldBadge.remove();
      if (newBadge) oldOpen.appendChild(newBadge);
    }
    if (oldCount.textContent !== newCount.textContent) oldCount.textContent = newCount.textContent;
  };

  CaCollection.prototype.syncDrawer = function (newDrawer) {
    var oldBody = this.drawer.querySelector('[data-cc-drawer-body]');
    var newBody = newDrawer.querySelector('[data-cc-drawer-body]');
    if (!oldBody || !newBody) return;

    var oldInputs = oldBody.querySelectorAll('input[id]');
    var newInputs = newBody.querySelectorAll('input[id]');
    var same = oldInputs.length === newInputs.length && Array.prototype.every.call(newInputs, function (input, i) {
      return input.id === oldInputs[i].id;
    });

    if (same) {
      Array.prototype.forEach.call(newInputs, function (input, i) {
        var old = oldInputs[i];
        if (old.type === 'checkbox') {
          old.checked = input.hasAttribute('checked');
          old.disabled = input.hasAttribute('disabled');
          var oldLabel = old.closest('.cc-check');
          var newLabel = input.closest('.cc-check');
          if (oldLabel && newLabel) {
            oldLabel.className = newLabel.className;
            var oldNum = oldLabel.querySelector('.cc-check__count');
            var newNum = newLabel.querySelector('.cc-check__count');
            if (oldNum && newNum) oldNum.textContent = newNum.textContent;
          }
        } else if (old !== document.activeElement) {
          old.value = input.getAttribute('value') || '';
        }
      });
      var oldSort = oldBody.querySelector('input[name="sort_by"]');
      var newSort = newBody.querySelector('input[name="sort_by"]');
      if (oldSort && newSort) oldSort.value = newSort.value;
    } else {
      var scroll = oldBody.scrollTop;
      oldBody.replaceWith(newBody);
      newBody.scrollTop = scroll;
    }

    var oldApply = this.drawer.querySelector('[data-cc-apply]');
    var newApply = newDrawer.querySelector('[data-cc-apply]');
    if (oldApply && newApply) oldApply.textContent = newApply.textContent;
    var oldClear = this.drawer.querySelector('[data-cc-drawer-clear]');
    var newClear = newDrawer.querySelector('[data-cc-drawer-clear]');
    if (oldClear && newClear) oldClear.setAttribute('href', newClear.getAttribute('href'));
  };

  /* ---------- Load more ---------- */

  CaCollection.prototype.loadMore = function (button) {
    var self = this;
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');

    this.fetchSection(button.href)
      .then(function (doc) {
        var grid = self.root.querySelector('[data-cc-grid]');
        var more = self.root.querySelector('[data-cc-more]');
        var items = doc.querySelectorAll('[data-cc-grid] > .cc-grid__item');
        if (!grid || !items.length) throw new Error('No items');

        var firstNew = null;
        Array.prototype.forEach.call(items, function (item, i) {
          var node = document.importNode(item, true);
          node.classList.add('is-new');
          node.style.animationDelay = Math.min(i, 8) * 40 + 'ms';
          grid.appendChild(node);
          if (!firstNew) firstNew = node;
        });

        var newMore = doc.querySelector('[data-cc-more]');
        if (more && newMore) {
          more.replaceWith(document.importNode(newMore, true));
        } else if (more) {
          more.remove();
        }

        var link = firstNew && firstNew.querySelector('.cc-card__title a');
        if (link) link.focus({ preventScroll: true });
      })
      .catch(function () {
        location.assign(cleanUrl(button.href).href);
      });
  };

  /* ---------- Swatches ---------- */

  CaCollection.prototype.selectSwatch = function (swatch) {
    var card = swatch.closest('[data-cc-card]');
    if (!card) return;

    var list = swatch.closest('.cc-swatches');
    if (list) {
      list.querySelectorAll('[data-cc-swatch][aria-current]').forEach(function (el) {
        el.removeAttribute('aria-current');
      });
    }
    swatch.setAttribute('aria-current', 'true');

    var img = card.querySelector('[data-cc-img]');
    if (img) {
      img.srcset = swatch.getAttribute('data-srcset');
      img.src = swatch.getAttribute('data-src');
      img.alt = swatch.getAttribute('data-alt') || '';
    }

    // The card now shows one specific variant, so stop revealing the product's second photo on
    // hover — it may well belong to another colour.
    card.classList.add('is-swapped');

    var url = swatch.getAttribute('data-url');
    card.querySelectorAll('[data-cc-link]').forEach(function (link) {
      link.setAttribute('href', url);
    });

    var soldOut = swatch.getAttribute('data-soldout') === '1';
    card.classList.toggle('is-soldout', soldOut);
    var badge = card.querySelector('[data-cc-badge]');
    if (badge) badge.hidden = !soldOut;

    var price = card.querySelector('[data-cc-price]');
    if (price) {
      var labels = card.querySelector('[data-cc-labels]');
      var now = swatch.getAttribute('data-price');
      var was = swatch.getAttribute('data-compare');
      price.textContent = '';
      if (was) {
        price.appendChild(span('cc-vh', labels ? labels.getAttribute('data-sale') : ''));
        price.appendChild(span('cc-price__now', now));
        price.appendChild(span('cc-vh', labels ? labels.getAttribute('data-regular') : ''));
        var s = document.createElement('s');
        s.className = 'cc-price__was';
        s.textContent = was;
        price.appendChild(s);
      } else {
        price.appendChild(span('cc-price__now', now));
      }
    }
  };

  function span(className, text) {
    var el = document.createElement('span');
    el.className = className;
    el.textContent = text;
    return el;
  }

  /* ---------- Boot ---------- */

  var instances = new Map();

  function init(scope) {
    (scope || document).querySelectorAll('[data-cc]').forEach(function (root) {
      if (!instances.has(root)) instances.set(root, new CaCollection(root));
    });
  }

  function teardown(scope) {
    instances.forEach(function (instance, root) {
      if (!scope || scope.contains(root)) {
        instance.destroy();
        instances.delete(root);
      }
    });
  }

  window.CaCollection = CaCollection;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:section:unload', function (event) { teardown(event.target); });
})();
